import {
  useState,
  useEffect,
  useRef,
} from 'react';

import Box from '@mui/material/Box';

import { SxProps } from '@mui/material';
import {
  Map,
  AdvancedMarker,
  useMapsLibrary
} from '@vis.gl/react-google-maps';

import { GOOGLE_MAP_ID } from '../../utils/GoogleMapsUtils';
import { useOnlineStatus } from '../../utils/useOnlineStatus';

import GratiMapFallback from './GratiMapFallback';

const containerStyle = {
  width: '100%',
  height: '100%',
};

const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 };

export interface GratiMapProps {
  placeId: string;
  sx?: SxProps;
}

export default function GratiMap(props: GratiMapProps) {
  const { placeId, sx } = props;
  const { isEmulator, isOnline } = useOnlineStatus();
  const placesLib = useMapsLibrary('places');
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
  // Guard so we only fetch once per placeId, even if placesLib reference is unstable
  const fetchedForPlaceIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOnline || isEmulator || !placeId || !placesLib) return;
    if (fetchedForPlaceIdRef.current === placeId) return;

    fetchedForPlaceIdRef.current = placeId;
    const getPlaceDetails = async () => {
      const place = new placesLib.Place({ id: placeId });
      await place.fetchFields({ fields: ['location'] });
      if (place.location) {
        setMapCenter({ lat: place.location.lat(), lng: place.location.lng() });
      } else {
        console.error('Error getting place location for placeId:', placeId);
      }
    };
    getPlaceDetails();
  }, [placeId, placesLib, isOnline, isEmulator]);

  return (
    <Box sx={sx}>
      {isOnline ? (
        <Map
          style={containerStyle}
          center={mapCenter || DEFAULT_CENTER}
          zoom={16}
          mapId={GOOGLE_MAP_ID}
        >
          {mapCenter && <AdvancedMarker position={mapCenter} />}
        </Map>
      ) : (
        <GratiMapFallback />
      )}
    </Box>
  );
};
